import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Clock, Loader2 } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { CATEGORIES, categoryBySlug, readingTime } from "@/articlesData";
import { DIAG_URL } from "@/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1601570682455-bdd0b87f0cfa?crop=entropy&cs=srgb&fm=jpg&q=85";

const ArticleCard = ({ a, big, testid }) => {
  const cat = categoryBySlug(a.category);
  return (
    <Link
      to={`/conteudo/${a.slug}`}
      data-testid={testid}
      className={`group relative flex flex-col overflow-hidden border border-white/10 bg-navy/40 transition-colors duration-300 hover:border-brand-orange/60 ${big ? "lg:col-span-2" : ""}`}
    >
      <div className={`relative overflow-hidden ${big ? "aspect-[16/8]" : "aspect-[16/9]"}`}>
        <img
          src={a.image}
          alt={a.imageAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
        <span className="absolute left-5 top-5 border border-white/20 bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-orange backdrop-blur-sm">
          {cat?.name}
        </span>
      </div>
      <div className="flex flex-1 flex-col justify-between gap-5 p-6 lg:p-7">
        <h3 className={`font-display font-bold leading-tight tracking-tight text-white transition-colors duration-300 group-hover:text-brand-orange ${big ? "text-2xl lg:text-3xl" : "text-xl"}`}>
          {a.title}
        </h3>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <Clock size={12} /> {readingTime(a)} min de leitura
          </span>
          <ArrowUpRight size={16} className="text-slate-500 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-brand-orange" />
        </div>
      </div>
    </Link>
  );
};

export default function Conteudo() {
  const [params, setParams] = useSearchParams();
  const active = params.get("categoria") || "";
  const [fading, setFading] = useState(false);
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then(({ data }) => setArticles(data.articles))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <main data-testid="conteudo-error" className="flex min-h-[70vh] flex-col items-center justify-center bg-ink px-6 pt-32 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Não foi possível carregar o conteúdo agora.</p>
      </main>
    );
  }

  if (!articles) {
    return (
      <main data-testid="conteudo-loading" className="flex min-h-[70vh] items-center justify-center gap-3 bg-ink pt-32 text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Carregando textos...
      </main>
    );
  }

  const filtered = active ? articles.filter((a) => a.category === active) : articles;
  const latest = articles.slice(0, 3);
  const [first, ...rest] = latest;

  const pickCategory = (slug) => {
    setFading(true);
    setTimeout(() => {
      setParams(slug ? { categoria: slug } : {});
      setFading(false);
      document.getElementById("biblioteca")?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      data-testid="conteudo-page"
      className="bg-ink"
    >
      {/* HERO */}
      <section data-testid="conteudo-hero" className="noise relative overflow-hidden bg-ink pb-16 pt-36 lg:pb-20 lg:pt-48">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" aria-hidden className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <SectionLabel>Conteúdo &amp; Análises</SectionLabel>
          <Reveal delay={0.15}>
            <h1 className="mt-8 max-w-4xl font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Ideias para marcas{" "}
              <span className="font-serif font-medium italic tracking-normal text-brand-orange">em movimento.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Análises e pontos de vista da FUCHS sobre posicionamento, crescimento, mercado e as decisões que dão direção a uma marca.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ÚLTIMOS TEXTOS */}
      <section data-testid="conteudo-latest" className="bg-ink pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Últimos textos{" "}
              <button
                onClick={() => document.getElementById("categorias")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="conteudo-goto-categories"
                className="ml-2 align-middle font-mono text-[11px] font-normal uppercase tracking-[0.22em] text-brand-orange transition-colors hover:text-white"
              >
                ou navegue por categoria ↓
              </button>
            </h2>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 sm:block">
              {articles.length} {articles.length === 1 ? "publicação" : "publicações"}
            </span>
          </Reveal>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {first && (
              <Reveal delay={0.1} className="lg:col-span-2">
                <ArticleCard a={first} big testid={`conteudo-featured-${first.slug}`} />
              </Reveal>
            )}
            <div className="grid gap-6">
              {rest.map((a, i) => (
                <Reveal key={a.slug} delay={0.2 + i * 0.1}>
                  <ArticleCard a={a} testid={`conteudo-featured-${a.slug}`} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIAS */}
      <section id="categorias" data-testid="conteudo-categories" className="bg-paper py-24 text-navy lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionLabel>Navegue por tema</SectionLabel>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Seis lentes sobre{" "}
              <span className="font-serif font-medium italic text-brand-orange">o mesmo ofício.</span>
            </h2>
          </Reveal>
          <div className="mt-12 border-t border-navy/10">
            {CATEGORIES.map((c, i) => {
              const count = articles.filter((a) => a.category === c.slug).length;
              const isActive = active === c.slug;
              return (
                <Reveal key={c.slug} delay={i * 0.05}>
                  <button
                    onClick={() => pickCategory(isActive ? "" : c.slug)}
                    data-testid={`category-${c.slug}`}
                    className={`group flex w-full items-center gap-6 border-b border-navy/10 py-6 text-left transition-colors duration-300 hover:bg-navy/[0.03] lg:gap-10 ${isActive ? "bg-navy/[0.04]" : ""}`}
                  >
                    <span className={`font-mono text-xs tracking-[0.2em] ${isActive ? "text-brand-orange" : "text-navy/40"}`}>
                      0{i + 1}
                    </span>
                    <div className="flex-1">
                      <h3 className={`font-display text-xl font-bold tracking-tight transition-colors duration-300 group-hover:text-brand-orange sm:text-2xl ${isActive ? "text-brand-orange" : ""}`}>
                        {c.name}
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">{c.desc}</p>
                    </div>
                    <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-navy/40 sm:block">
                      {count} {count === 1 ? "texto" : "textos"}
                    </span>
                    <ArrowUpRight size={18} className={`shrink-0 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${isActive ? "text-brand-orange" : "text-navy/40 group-hover:text-brand-orange"}`} />
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BIBLIOTECA (filtrada por categoria) */}
      <section id="biblioteca" data-testid="conteudo-library" className="bg-ink py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <SectionLabel>Biblioteca</SectionLabel>
              <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                {active ? categoryBySlug(active)?.name : "Todos os textos"}
              </h2>
            </div>
            {active && (
              <button
                onClick={() => pickCategory("")}
                data-testid="conteudo-clear-filter"
                className="border border-white/20 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                Limpar filtro
              </button>
            )}
          </Reveal>
          <div className={`mt-10 grid gap-6 transition-opacity duration-200 sm:grid-cols-2 lg:grid-cols-3 ${fading ? "opacity-0" : "opacity-100"}`}>
            {filtered.map((a) => (
              <ArticleCard key={a.slug} a={a} testid={`conteudo-card-${a.slug}`} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p data-testid="conteudo-empty" className="mt-10 border border-white/10 bg-navy/30 px-8 py-14 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
              Novos textos nesta categoria em breve
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="conteudo-cta" className="noise relative overflow-hidden border-t border-white/5 bg-ink py-24 lg:py-28">
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-4xl">
              Quer saber onde a sua{" "}
              <span className="font-serif font-medium italic text-brand-orange">marca está hoje?</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-400">
              Comece pelo Diagnóstico de Maturidade 4D — gratuito, online e com leitura imediata do seu radar estratégico.
            </p>
            <div className="mt-10">
              <Link
                to={DIAG_URL}
                data-testid="conteudo-cta-diagnostico"
                className="group inline-flex items-center gap-3 bg-brand-orange px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-red"
              >
                Fazer o diagnóstico gratuito
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
